<template>
    <div v-if="ratesData" class="mt-4">
        <busy-modal :show="loading" />
        <b-row class="w-100 no-gutters">
            <b-col md="6" class="pr-md-2">
                <form-select
                    v-model="ratesData.use_weekly_rates"
                    :options="ratesOptions"
                    floatingLabel="Weekly Rates"
                    @change="setRatesSettings"
                    :leftIcon="['fas', 'arrow-right']"
                />
            </b-col>
            <b-col md="6">
                <form-select
                    v-model="ratesData.use_monthly_rates"
                    :options="ratesOptions"
                    floatingLabel="Monthly Rates"
                    @change="setRatesSettings"
                    :leftIcon="['fas', 'arrow-right']"
                />
            </b-col>
        </b-row>
        <b-row class="w-100 no-gutters">
            <b-col md="6" class="pr-md-2">
                <form-select
                    v-model="ratesData.use_per_person_rates"
                    :options="ratesOptions"
                    floatingLabel="Per Person Rates"
                    @change="setRatesSettings"
                    :leftIcon="['fas', 'arrow-right']"
                />
            </b-col>
            <b-col md="6">
                <form-select
                    v-model="ratesData.use_children_rates"
                    :options="totalGuestOptions"
                    :leftIcon="['fas', 'arrow-right']"
                    floatingLabel="Total Guests Calculation"
                    @change="setRatesSettings"
                />
            </b-col>
        </b-row>
        <b-row class="w-100 no-gutters">
            <b-col
                :class="{ 'pr-md-2': ratesData.require_guest_names }"
                :md="ratesData.require_guest_names ? 6 : 12"
            >
                <form-select
                    v-model="ratesData.require_guest_names"
                    :options="getGuestOptions('names')"
                    floatingLabel="Guest Names"
                    @change="setRatesSettings"
                    :leftIcon="['fas', 'arrow-right']"
                />
            </b-col>
            <b-col class="pr-md-2" v-if="ratesData.require_guest_names" md="3">
                <form-select
                    v-model="ratesData.require_guest_age"
                    :options="getGuestOptions('ages')"
                    :leftIcon="['fas', 'arrow-right']"
                    floatingLabel="Guest Ages"
                    @change="setRatesSettings"
                />
            </b-col>
            <b-col md="3" v-if="ratesData.require_guest_names">
                <form-select
                    v-model="ratesData.require_guest_email"
                    :options="getGuestOptions('emails')"
                    :leftIcon="['fas', 'arrow-right']"
                    floatingLabel="Guest Email"
                    @change="setRatesSettings"
                />
            </b-col>
        </b-row>
        <b-row class="w-100 no-gutters">
            <b-col md="6">
                <b-card class="template rate-question-card">
                    <template #header
                        >Important! Please answer the questions below.
                    </template>
                    <b-card-body>
                        <form-select
                            label="Guest books a stay that is > than a week or month. How to calculate total rate?"
                            :options="howToCalculateRateOptionsIfLessThanWeek"
                            v-model="ratesData.prorate_partial_period"
                            @change="setRatesSettings"
                        />
                        <form-select
                            label="Guest books a stay that spans two different rate periods. How to calculate rate?"
                            :options="
                                howToCalculateRateOptionsIfDiffRatePeriods
                            "
                            v-model="ratesData.several_rates_available"
                            @change="setRatesSettings"
                        />
                        <form-select
                            label="How to calculate Monthly Rate month length?"
                            :options="howToCalculateRateOptionsIfMonthlyRate"
                            v-model="ratesData.monthly_length_calculation"
                            @change="setRatesSettings"
                        />
                    </b-card-body> </b-card></b-col
        ></b-row>
    </div>
</template>
         

<script>
import api from '../../../../api';
import { RENTAL_PROPERTY_SETUP_RATES_ENDPOINT } from '../../../../endpoints';
import { formatResponseError } from '../../../../utils/api';
import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
import FormSelect from '../../../generic/form-select/form-select.vue';
export default {
    props: {
        ratesData: {
            type: Object,
            default: null,
        },
    },
    components: {
        FormSelect,
        BusyModal,
    },
    data() {
        return {
            loading: false,
        };
    },
    inject: ['propertyId'],
    computed: {
        ratesOptions() {
            return [
                { text: 'Disabled', value: false },
                { text: 'Enabled', value: true },
            ];
        },
        howToCalculateRateOptionsIfMonthlyRate() {
            return [
                {
                    value: 'RESERVATION_START_MONTH_LENGTH',
                    text: 'Reservation start day month length',
                },
                {
                    value: 'ALWAYS_30_DAYS',
                    text: 'Always 30 days',
                },
                {
                    value: 'ALWAYS_28_DAYS',
                    text: 'Always 28 days',
                },
            ];
        },
        howToCalculateRateOptionsIfLessThanWeek() {
            return [
                {
                    value: 'USE_DAILY',
                    text: 'Weekly/monthly rental rate + one night daily rental rate',
                },
                {
                    value: 'PRORATE',
                    text: 'Weekly/monthly rental rate + pro-rate of weekly/monthly rental rate',
                },
            ];
        },
        howToCalculateRateOptionsIfDiffRatePeriods() {
            return [
                {
                    value: 'LOWER',
                    text: 'Choose lower rate',
                },
                {
                    value: 'HIGHER',
                    text: 'Choose higher rate',
                },
                {
                    value: 'COMBINE',
                    text: 'Prorate each rate',
                },
            ];
        },
        totalGuestOptions() {
            return [
                {
                    text: 'Children are excluded when calculating Total Guests',
                    value: false,
                },
                {
                    text: 'Children are included when calculating Total Guests',
                    value: true,
                },
            ];
        },
    },
    methods: {
        async setRatesSettings() {
            if (!this.loading) {
                try {
                    this.loading = true;
                    await api.post(RENTAL_PROPERTY_SETUP_RATES_ENDPOINT(this.propertyId).setRatesSettings, {
                        ...this.ratesData,
                    });
                    this.$emit('reload-all-data');
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not set rates settings',
                    });
                } finally {
                    this.loading = false;
                }
            }
        },
        getGuestOptions(string) {
            return [
                { text: `Do not require guest ${string}`, value: false },
                { text: `Require guest ${string}`, value: true },
            ];
        },
    },
};
</script>